import { useState, useEffect, useRef } from "react";
import { api } from "@/api/client";
import { errorCode, errorMessage } from "@/lib/errors";
import { formatCents } from "@/lib/money";
import { loadSquare } from "@/lib/square";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Lock, CreditCard, Building2, Banknote, Tag, CheckCircle2, X } from "lucide-react";

// Tailwind only emits classes it can see, so the column count is a lookup.
const GRID_COLS = { 1: "grid-cols-1", 2: "grid-cols-2", 3: "grid-cols-3" };

/**
 * Checkout. The server owns every money decision: it recomputes the price,
 * validates the coupon, holds the seats and picks the deposit. This form only
 * collects what it needs and shows the quote it gets back.
 */
export default function PaymentModal({ event, quantity, selectedOption, activePriceCents, onSuccess, onClose, isAdmin }) {
  const [enabled, setEnabled] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("card"); // "card" | "ach" | "inperson"
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");
  const [sdkReady, setSdkReady] = useState(false);
  const [squareUnavailable, setSquareUnavailable] = useState(false);
  const cardRef = useRef(null);

  // ACH fields
  const [bankName, setBankName] = useState("");
  const [routingNumber, setRoutingNumber] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [routingError, setRoutingError] = useState("");

  // In-person fields
  const [inPersonType, setInPersonType] = useState("cash");
  const [checkNumber, setCheckNumber] = useState("");

  // Admin-assisted sales: who the ticket is really for.
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");

  // Attendee names
  const requireNames = !!event.require_attendee_names;
  const [attendeeNames, setAttendeeNames] = useState(Array.from({ length: quantity }, () => ({ first_name: "", last_name: "" })));

  // Coupon: the quote comes from the server.
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState("");
  const [couponLoading, setCouponLoading] = useState(false);

  const priceCents = activePriceCents ?? event.price_cents ?? 0;
  const subtotalCents = priceCents * quantity;
  const discountCents = appliedCoupon?.discount_cents ?? 0;
  const totalCents = Math.max(0, subtotalCents - discountCents);

  // Which methods to offer. Card needs Square configured; ACH and in-person are admin tools.
  useEffect(() => {
    let cancelled = false;
    api.settings
      .checkout()
      .then((methods) => {
        if (cancelled) return;
        setEnabled(methods);
        const first = methods.card ? "card" : isAdmin && methods.ach ? "ach" : isAdmin && (methods.cash || methods.check) ? "inperson" : "card";
        setPaymentMethod(first);
        if (!methods.cash && methods.check) setInPersonType("check");
      })
      .catch((err) => {
        if (cancelled) return;
        setEnabled({ card: true, ach: true, cash: true, check: true });
        setError(errorMessage(err));
      });
    return () => {
      cancelled = true;
    };
  }, [isAdmin]);

  // Mount the Square card form once the card tab is showing.
  useEffect(() => {
    if (!enabled || paymentMethod !== "card" || cardRef.current) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const config = await api.settings.square();
        const Square = await loadSquare(config.environment);
        if (cancelled) return;
        const payments = Square.payments(config.application_id, config.location_id);
        const card = await payments.card();
        if (cancelled) return;
        await card.attach("#sq-card-container");
        cardRef.current = card;
        setSdkReady(true);
      } catch (err) {
        if (cancelled) return;
        if (errorCode(err) === "SQUARE_NOT_CONFIGURED") {
          setSquareUnavailable(true);
          setError("Card payments are not available right now. Please choose another payment method or contact us.");
        } else {
          console.error("Square init error:", err);
          setError("Payment system is not available right now. Please try again later.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, paymentMethod]);

  useEffect(() => {
    return () => {
      if (cardRef.current) cardRef.current.destroy().catch(() => {});
    };
  }, []);

  const handleApplyCoupon = async () => {
    const code = couponCode.trim().toUpperCase();
    if (!code) return;
    setCouponLoading(true);
    setCouponError("");
    setAppliedCoupon(null);
    try {
      const quote = await api.coupons.validate({ event_id: event.id, ticket_option_id: selectedOption?.id ?? null, quantity, code });
      setAppliedCoupon({ ...quote.coupon, discount_cents: quote.discount_cents });
    } catch (err) {
      setCouponError(errorCode(err) === "COUPON_INVALID" ? errorMessage(err) : errorMessage(err, "Could not validate coupon. Please try again."));
    }
    setCouponLoading(false);
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode("");
    setCouponError("");
  };

  const validateNames = () => {
    if (!requireNames) return true;
    for (let i = 0; i < attendeeNames.length; i++) {
      const n = attendeeNames[i];
      if (!n.first_name.trim() || !n.last_name.trim()) {
        setError(`Please enter the first and last name for ticket #${i + 1}.`);
        return false;
      }
    }
    return true;
  };

  const baseBody = () => ({
    event_id: event.id,
    ticket_option_id: selectedOption?.id ?? null,
    quantity,
    coupon_code: appliedCoupon?.code ?? null,
    attendee_names: requireNames ? attendeeNames : null,
  });

  const customerFields = () => ({
    customer_name: customerName.trim() || null,
    customer_email: customerEmail.trim() || null,
  });

  const finish = async (fn) => {
    setProcessing(true);
    setError("");
    try {
      const order = await fn();
      onSuccess(order);
    } catch (err) {
      setError(errorMessage(err, "Payment failed. Please try again."));
      setProcessing(false);
    }
  };

  const handleCardPay = async () => {
    if (!cardRef.current) return;
    if (!validateNames()) return;
    setProcessing(true);
    setError("");
    const tokenResult = await cardRef.current.tokenize();
    if (tokenResult.status !== "OK") {
      setError(tokenResult.errors?.map((e) => e.message).join(", ") || "Card error. Please try again.");
      setProcessing(false);
      return;
    }
    await finish(() => api.checkout.card({ ...baseBody(), source_id: tokenResult.token }));
  };

  const handleInPersonPay = async () => {
    if (!validateNames()) return;
    if (inPersonType === "check" && !checkNumber.trim()) {
      setError("Please enter the check number.");
      return;
    }
    await finish(() =>
      api.checkout.inPerson({ ...baseBody(), ...customerFields(), in_person_type: inPersonType, check_number: inPersonType === "check" ? checkNumber.trim() : null }),
    );
  };

  const handleAchPay = async () => {
    if (!validateNames()) return;
    setRoutingError("");
    if (!bankName.trim()) return setError("Please enter your bank name.");
    if (routingNumber.length !== 9) return setRoutingError("Routing number must be exactly 9 digits.");
    if (!accountNumber.trim()) return setError("Please enter your account number.");
    await finish(() =>
      api.checkout.ach({ ...baseBody(), ...customerFields(), bank_name: bankName.trim(), routing_number: routingNumber, account_number: accountNumber }),
    );
  };

  const methods = enabled ?? { card: false, ach: false, cash: false, check: false };
  const buttons = [
    methods.card && { key: "card", label: "Credit / Debit", icon: <CreditCard className="h-4 w-4" /> },
    methods.ach && isAdmin && { key: "ach", label: "ACH / Bank", icon: <Building2 className="h-4 w-4" /> },
    isAdmin && (methods.cash || methods.check) && { key: "inperson", label: "In Person", icon: <Banknote className="h-4 w-4" /> },
  ].filter(Boolean);
  const nothingAvailable = enabled && buttons.length === 0;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md flex flex-col max-h-[90vh]">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-indigo-600" />
            Complete Your Purchase
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 overflow-y-auto flex-1 pr-1">
          {/* Order Summary */}
          <div className="bg-indigo-50 rounded-xl p-4">
            <p className="font-semibold text-gray-900 text-sm">{event.title}</p>
            {selectedOption && <p className="text-xs text-indigo-600 mt-0.5">{selectedOption.name}</p>}
            <div className="flex justify-between items-center mt-2">
              <span className="text-sm text-gray-500">{quantity} × {formatCents(priceCents)}</span>
              <span className={`font-bold text-lg ${appliedCoupon ? "line-through text-gray-400 text-base" : "text-indigo-600"}`}>{formatCents(subtotalCents)}</span>
            </div>
            {appliedCoupon && (
              <div className="flex justify-between items-center mt-1">
                <span className="text-sm text-green-600 font-medium">Discount ({appliedCoupon.code})</span>
                <span className="font-bold text-green-600">-{formatCents(discountCents)}</span>
              </div>
            )}
            {appliedCoupon && (
              <div className="flex justify-between items-center mt-1 pt-1 border-t border-indigo-200">
                <span className="text-sm font-semibold text-gray-700">Total</span>
                <span className="font-bold text-indigo-600 text-lg">{formatCents(totalCents)}</span>
              </div>
            )}
          </div>

          {/* Coupon Code */}
          <div className="space-y-1">
            <Label className="text-sm font-medium text-gray-700">Discount Code</Label>
            {appliedCoupon ? (
              <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-xl px-3 py-2">
                <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0" />
                <span className="text-sm text-green-700 font-medium flex-1">
                  <span className="font-mono">{appliedCoupon.code}</span> applied —{" "}
                  {appliedCoupon.discount_type === "percentage" ? `${appliedCoupon.discount_value}% off` : `${formatCents(appliedCoupon.discount_value)} off`}
                </span>
                <button onClick={handleRemoveCoupon} className="text-green-500 hover:text-green-700" aria-label="Remove coupon">
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Tag className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    value={couponCode}
                    onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); setCouponError(""); }}
                    onKeyDown={(e) => e.key === "Enter" && handleApplyCoupon()}
                    placeholder="Enter code"
                    className="pl-9 font-mono uppercase"
                  />
                </div>
                <Button onClick={handleApplyCoupon} disabled={couponLoading || !couponCode.trim()} variant="outline" className="px-4">
                  {couponLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply"}
                </Button>
              </div>
            )}
            {couponError && <p className="text-xs text-red-500">{couponError}</p>}
          </div>

          {nothingAvailable && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <p className="text-sm font-semibold text-red-700 mb-1">Payment Not Available</p>
              <p className="text-xs text-red-600">No payment methods are enabled right now. Please contact the administrator.</p>
            </div>
          )}

          {/* Payment Method Toggle */}
          {buttons.length > 1 && (
            <div className={`grid gap-2 ${GRID_COLS[buttons.length] ?? "grid-cols-1"}`}>
              {buttons.map(({ key, label, icon }) => (
                <button
                  key={key}
                  onClick={() => { setPaymentMethod(key); setError(""); }}
                  className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl border-2 text-sm font-medium transition-colors ${
                    paymentMethod === key ? "border-indigo-600 bg-indigo-50 text-indigo-700" : "border-gray-200 text-gray-500 hover:border-gray-300"
                  }`}
                >
                  {icon} {label}
                </button>
              ))}
            </div>
          )}

          {/* Attendee Names */}
          {requireNames && (
            <div className="space-y-3">
              <p className="text-sm font-semibold text-gray-700">Attendee Names <span className="text-red-500">*</span></p>
              {attendeeNames.map((n, i) => (
                <div key={i} className="bg-gray-50 rounded-xl p-3 space-y-2">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Ticket #{i + 1}</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs text-gray-600">First Name</Label>
                      <Input
                        value={n.first_name}
                        onChange={(e) => {
                          const updated = [...attendeeNames];
                          updated[i] = { ...updated[i], first_name: e.target.value };
                          setAttendeeNames(updated);
                        }}
                        placeholder="First"
                        className="mt-1 h-8 text-sm"
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-600">Last Name</Label>
                      <Input
                        value={n.last_name}
                        onChange={(e) => {
                          const updated = [...attendeeNames];
                          updated[i] = { ...updated[i], last_name: e.target.value };
                          setAttendeeNames(updated);
                        }}
                        placeholder="Last"
                        className="mt-1 h-8 text-sm"
                        required
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Admin-assisted sale: the customer */}
          {isAdmin && paymentMethod !== "card" && (
            <div className="bg-gray-50 rounded-xl p-3 space-y-2">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Customer (optional)</p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs text-gray-600">Name</Label>
                  <Input value={customerName} onChange={(e) => setCustomerName(e.target.value)} placeholder="Customer name" className="mt-1 h-8 text-sm" />
                </div>
                <div>
                  <Label className="text-xs text-gray-600">Email (for the ticket)</Label>
                  <Input type="email" value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} placeholder="customer@example.com" className="mt-1 h-8 text-sm" />
                </div>
              </div>
              <p className="text-xs text-gray-400">Leave blank to record the sale under your own account.</p>
            </div>
          )}

          {/* Card Payment */}
          {paymentMethod === "card" && (
            <>
              {loading && !squareUnavailable && (
                <div className="flex flex-col items-center justify-center py-10 gap-3">
                  <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
                  <p className="text-sm text-gray-400">Loading secure payment form...</p>
                </div>
              )}
              <div style={{ display: loading || squareUnavailable ? "none" : "block" }}>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Card Details</label>
                <div id="sq-card-container" className="border border-gray-200 rounded-xl p-3 min-h-[80px]" />
              </div>
            </>
          )}

          {/* In-Person Payment (Admin only) */}
          {paymentMethod === "inperson" && (
            <div className="space-y-3">
              <div>
                <Label className="text-sm font-medium text-gray-700 mb-2 block">Payment Type</Label>
                <div className={`grid gap-2 ${methods.cash && methods.check ? "grid-cols-2" : "grid-cols-1"}`}>
                  {methods.cash && (
                    <button
                      onClick={() => { setInPersonType("cash"); setCheckNumber(""); setError(""); }}
                      className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border-2 text-sm font-medium transition-colors ${
                        inPersonType === "cash" ? "border-green-600 bg-green-50 text-green-700" : "border-gray-200 text-gray-500 hover:border-gray-300"
                      }`}
                    >
                      <Banknote className="h-4 w-4" /> Cash
                    </button>
                  )}
                  {methods.check && (
                    <button
                      onClick={() => { setInPersonType("check"); setError(""); }}
                      className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border-2 text-sm font-medium transition-colors ${
                        inPersonType === "check" ? "border-green-600 bg-green-50 text-green-700" : "border-gray-200 text-gray-500 hover:border-gray-300"
                      }`}
                    >
                      <CreditCard className="h-4 w-4" /> Check
                    </button>
                  )}
                </div>
              </div>
              {inPersonType === "check" && (
                <div>
                  <Label htmlFor="check-number" className="text-sm font-medium text-gray-700">Check Number</Label>
                  <Input id="check-number" placeholder="Enter check number" value={checkNumber} onChange={(e) => setCheckNumber(e.target.value)} className="mt-1" />
                </div>
              )}
            </div>
          )}

          {/* ACH Payment */}
          {paymentMethod === "ach" && (
            <div className="space-y-3">
              <div>
                <Label htmlFor="bank-name" className="text-sm font-medium text-gray-700">Bank Name</Label>
                <Input id="bank-name" placeholder="e.g. Chase, Wells Fargo" value={bankName} onChange={(e) => setBankName(e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label htmlFor="routing-number" className="text-sm font-medium text-gray-700">Routing Number</Label>
                <Input
                  id="routing-number"
                  placeholder="9-digit routing number"
                  value={routingNumber}
                  inputMode="numeric"
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, "").slice(0, 9);
                    setRoutingNumber(val);
                    setRoutingError(val.length > 0 && val.length < 9 ? "Routing number must be exactly 9 digits." : "");
                  }}
                  className={`mt-1 ${routingError ? "border-red-400" : ""}`}
                  maxLength={9}
                />
                {routingError && <p className="text-xs text-red-500 mt-1">{routingError}</p>}
                <p className="text-xs text-gray-400 mt-1">{routingNumber.length}/9 digits</p>
              </div>
              <div>
                <Label htmlFor="account-number" className="text-sm font-medium text-gray-700">Account Number</Label>
                <Input id="account-number" placeholder="Account number" value={accountNumber} inputMode="numeric" onChange={(e) => setAccountNumber(e.target.value.replace(/\D/g, ""))} className="mt-1" />
              </div>
            </div>
          )}

          {error && <div className="bg-red-50 border border-red-100 text-red-700 text-sm px-4 py-3 rounded-xl">{error}</div>}

          <Button
            onClick={paymentMethod === "card" ? handleCardPay : paymentMethod === "ach" ? handleAchPay : handleInPersonPay}
            disabled={!enabled || nothingAvailable || (paymentMethod === "card" && (!sdkReady || processing)) || (paymentMethod !== "card" && processing)}
            className="w-full bg-indigo-600 hover:bg-indigo-700 h-12 text-base"
          >
            {processing ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Processing...</>
            ) : (
              <><Lock className="h-4 w-4 mr-2" />Pay {formatCents(totalCents)}</>
            )}
          </Button>

          <p className="text-xs text-center text-gray-400">
            {paymentMethod === "card" ? "Secured by Square · Your card info is never stored" : paymentMethod === "ach" ? "ACH payments are processed securely" : "In-person payment — admin use only"}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
